import { Button } from "@datamole/wds-component-button";
import { Card } from "@datamole/wds-component-card";
import { Combobox, useCombobox } from "@datamole/wds-component-combobox";
import { Dialog } from "@datamole/wds-component-dialog";
import { faPlus, faRotateRight, faChair, faRemove } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContext, useMemo, useState } from "react";

import { seatConfigurationService } from "@/features/administration/seat-configuration/seat-configuration.service";
import { useRole } from "@/features/auth/role.hooks";
import { InfoPopoverContext, EmptySeatTrigger } from "@/features/info-popover/popover.context";
import { storageService } from "@/features/storage/storage.service";
import { useStorageSubscription } from "@/features/storage/use-storage-subscription";

type Props = {
    trigger: EmptySeatTrigger;
};

export const EmptySeatHeader = () => {
    return (
        <Card.Header.Description>
            <Card.Header.Description.Title style={{ fontSize: 24 }}>
                <FontAwesomeIcon icon={faChair} style={{ marginRight: 16, color: "var(--wds-color-primary-700)" }} />
                Empty seat
            </Card.Header.Description.Title>
        </Card.Header.Description>
    );
};

export const EmptySeatCard = (_props: Props) => {
    useStorageSubscription();

    return <div />;
};

const RotateSeatAction = ({ seatId }: { seatId: string }) => {
    const seat = storageService.getSeatById(seatId)!;
    const { setTrigger } = useContext(InfoPopoverContext);

    return (
        <Button
            onClick={() => {
                storageService.rotateSeat(seat.room.id, seat.id, (seat.theta + 90) % 360);
                setTrigger({ type: "empty-seat", seatId: seat.id });
            }}
        >
            <Button.Icon>
                <FontAwesomeIcon icon={faRotateRight} />
            </Button.Icon>
            <Button.Content>Rotate</Button.Content>
        </Button>
    );
};

const RemoveSeatAction = ({ seatId }: { seatId: string }) => {
    const seat = storageService.getSeatById(seatId)!;
    const { setTrigger } = useContext(InfoPopoverContext);

    return (
        <Button
            onClick={() => {
                storageService.removeSeat(seat.room.id, seat.id);
                setTrigger(null);
            }}
        >
            <Button.Icon>
                <FontAwesomeIcon icon={faRemove} />
            </Button.Icon>
            <Button.Content>Remove Seat</Button.Content>
        </Button>
    );
};

const AssignSeatAction = ({ seatId }: { seatId: string }) => {
    const employees = useMemo(() => storageService.getEmployees(), []);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);

    const assignableEmployees = useMemo(() => {
        return employees.filter((employee) => employee.seat === "Unassigned");
    }, [employees]);

    const employeeCombobox = useCombobox({
        options: assignableEmployees,
        value: selectedEmployeeId,
        onChange: setSelectedEmployeeId,
        getOptionLabel: (emp) => emp.name,
        getOptionValue: (emp) => emp.id,
    });

    const handleAssignSeat = () => {
        if (!selectedEmployeeId) return;
        seatConfigurationService.addEmployeeToSeat(seatId, selectedEmployeeId);
    };

    return (
        <Dialog>
            <Dialog.Trigger asChild>
                <Button>
                    <Button.Icon>
                        <FontAwesomeIcon icon={faPlus} />
                    </Button.Icon>
                    <Button.Content>Assign Seat</Button.Content>
                </Button>
            </Dialog.Trigger>
            <Dialog.Content>
                <Dialog.Content.Header>Assign Seat</Dialog.Content.Header>
                <Dialog.Content.Body>
                    <Combobox {...employeeCombobox.getRootProps()}>
                        <Combobox.Trigger>
                            <Combobox.Trigger.Value>
                                {employeeCombobox.valueOption
                                    ? employeeCombobox.valueOption.name
                                    : "Select Employee without an assigned seat"}
                            </Combobox.Trigger.Value>
                        </Combobox.Trigger>
                        <Combobox.Content {...employeeCombobox.getContentProps()}>
                            <Combobox.Content.SearchBox
                                placeholder="Search employee"
                                {...employeeCombobox.getSearchBoxProps()}
                            />
                            <Combobox.Content.List {...employeeCombobox.getListProps()}>
                                {employeeCombobox.options.length < 1 ? (
                                    <Combobox.Content.Info>No results found.</Combobox.Content.Info>
                                ) : (
                                    employeeCombobox.options.map((emp, index) => (
                                        <Combobox.Content.List.Option
                                            key={emp.id}
                                            {...employeeCombobox.getOptionProps(index)}
                                        >
                                            {emp.name}
                                        </Combobox.Content.List.Option>
                                    ))
                                )}
                            </Combobox.Content.List>
                        </Combobox.Content>
                    </Combobox>
                </Dialog.Content.Body>
                <Dialog.Content.Actions>
                    <Dialog.Content.Actions.Close asChild>
                        <Button variant="primary" onClick={handleAssignSeat}>
                            Submit
                        </Button>
                    </Dialog.Content.Actions.Close>
                    <Dialog.Content.Actions.Close asChild>
                        <Button>Cancel</Button>
                    </Dialog.Content.Actions.Close>
                </Dialog.Content.Actions>
            </Dialog.Content>
        </Dialog>
    );
};

export const EmptySeatActions = ({ trigger }: Props) => {
    const { isAdmin } = useRole();

    const actions = [];
    if (isAdmin) {
        actions.push(<AssignSeatAction key="assign-seat" seatId={trigger.seatId} />);
        actions.push(<RemoveSeatAction key="remove-seat" seatId={trigger.seatId} />);
        actions.push(<RotateSeatAction key="rotate-seat" seatId={trigger.seatId} />);
    }

    return <>{actions}</>;
};
